/**
 * Sticky Add to Cart
 *
 * Handles positioning of the sticky add to cart button on mobile devices
 * and updates button state based on variant availability.
 */
class StickyAddToCart {
  constructor() {
    this.stickyAddToCart = document.getElementById("sticky-add-to-cart");
    if (!this.stickyAddToCart || window.innerWidth > 749) return;

    this.init();
  }

  init() {
    // Find the product-info-accordion section specifically
    this.findTargetSection();

    if (this.targetSection) {
      // Initial positioning
      setTimeout(() => this.handleScroll(), 500);

      // Listen for scroll events
      window.addEventListener("scroll", this.handleScroll.bind(this));

      // Handle resize events
      window.addEventListener("resize", this.handleResize.bind(this));

      // Also recalculate on orientation change for mobile devices
      window.addEventListener(
        "orientationchange",
        this.handleScroll.bind(this)
      );

      // Recalculate when images or dynamic content loads
      window.addEventListener("load", this.handleScroll.bind(this));
      document.querySelectorAll("img").forEach((img) => {
        img.addEventListener("load", this.handleScroll.bind(this));
      });
    }

    // Update sticky button state when product variants change
    document.addEventListener("variant:changed", this.updateButtonState);
  }

  findTargetSection() {
    // Look specifically for the product-info-accordion
    this.targetSection = document.querySelector(".product-info-accordion");

    // If not found, look for other common sections as fallback
    if (!this.targetSection) {
      const fallbackSelectors = [
        ".product__accordion",
        ".product-recommendations",
        ".related-products",
        ".complementary-products",
      ];

      for (const selector of fallbackSelectors) {
        this.targetSection = document.querySelector(selector);
        if (this.targetSection) {
          break;
        }
      }
    }

    // Final fallback - use the last element in product info container
    if (!this.targetSection) {
      const productInfo = document.querySelector(".product__info-container");
      if (productInfo) {
        const children = productInfo.children;
        if (children.length > 0) {
          this.targetSection = children[children.length - 1];
        }
      }
    }
  }

  handleScroll() {
    if (!this.targetSection || !this.stickyAddToCart) return;

    const targetRect = this.targetSection.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // Calculate the position where we want the button to stick
    const targetTopFromDocument = targetRect.top + scrollTop;
    const targetBottomFromDocument = targetTopFromDocument + targetRect.height;
    const finalStickyPosition = targetBottomFromDocument + 10;

    // Define the range where we want the transition to happen
    const transitionStart = targetBottomFromDocument - windowHeight - 100; // Start transition 100px before
    const transitionEnd = targetBottomFromDocument - windowHeight + 50; // End transition 50px after

    if (scrollTop >= transitionEnd) {
      // Fully stuck position
      this.stickyAddToCart.classList.add("position-absolute");
      this.stickyAddToCart.style.position = "absolute";
      this.stickyAddToCart.style.top = `${finalStickyPosition}px`;
      this.stickyAddToCart.style.bottom = "auto";
      this.stickyAddToCart.style.transform = "translateY(0)";
    } else if (scrollTop >= transitionStart) {
      // Transition phase - gradually move between bottom and final position
      // This works for both directions (up and down scrolling)
      const progress =
        (scrollTop - transitionStart) / (transitionEnd - transitionStart);
      const easeProgress = this.easeInOutCubic(
        Math.max(0, Math.min(1, progress))
      ); // Clamp between 0 and 1

      // Always use fixed positioning during transition for smooth movement
      this.stickyAddToCart.classList.remove("position-absolute");
      this.stickyAddToCart.style.position = "fixed";
      this.stickyAddToCart.style.top = "auto";

      // Calculate smooth transition between bottom (0) and final position
      const bottomPosition = 0; // Start at bottom
      const targetDistanceFromBottom = Math.max(
        0,
        finalStickyPosition - scrollTop - windowHeight
      );

      // Interpolate between bottom position and target distance
      const currentBottom =
        bottomPosition + targetDistanceFromBottom * easeProgress;

      this.stickyAddToCart.style.bottom = `${Math.max(0, currentBottom)}px`;
      this.stickyAddToCart.style.transform = "translateY(0)";
    } else {
      // Normal floating position at bottom
      this.stickyAddToCart.classList.remove("position-absolute");
      this.stickyAddToCart.style.position = "fixed";
      this.stickyAddToCart.style.top = "auto";
      this.stickyAddToCart.style.bottom = "0";
      this.stickyAddToCart.style.transform = "translateY(0)";
    }
  }

  // Easing function for smooth animation
  easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  handleResize() {
    if (window.innerWidth <= 749) {
      this.handleScroll();
    } else {
      // Reset if no longer in mobile view
      if (this.stickyAddToCart) {
        this.stickyAddToCart.classList.remove("position-absolute");
        this.stickyAddToCart.style.position = "fixed";
        this.stickyAddToCart.style.top = "auto";
        this.stickyAddToCart.style.bottom = "0";
      }
    }
  }

  static updateButtonState(event) {
    const variant = event.detail.variant;
    const stickyButton = document.querySelector(".sticky-button");

    if (stickyButton) {
      if (variant && variant.available) {
        stickyButton.disabled = false;
        stickyButton.innerHTML =
          "<span>" + window.variantStrings.addToCart + "</span>";
      } else {
        stickyButton.disabled = true;
        stickyButton.innerHTML =
          "<span>" + window.variantStrings.soldOut + "</span>";
      }
    }
  }
}

// Initialize on DOM load
document.addEventListener("DOMContentLoaded", function () {
  new StickyAddToCart();
});
